<script lang="ts">
  import { CATEGORY_META, categoryLabel, type DisplayView } from '@banger/shared';
  import { spinTick } from '../lib/sfx';
  import { trackHeading } from '../lib/trackCopy';
  import PatternGrid from '../shared/PatternGrid.svelte';

  let { view }: { view: DisplayView } = $props();

  const meta = $derived(view.category ? CATEGORY_META[view.category] : null);
  // The ball settles a little before the spin phase ends (fast dev rooms included).
  // svelte-ignore state_referenced_locally
  const spinMs = Math.max(400, ((view.deadline ?? view.serverNow) - view.serverNow) * 0.75);
  let landed = $state(false);

  $effect(() => {
    // Ticks slow down like a wheel losing speed, then the category lands.
    let elapsed = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (elapsed >= spinMs) { landed = true; return; }
      spinTick();
      const gap = 55 + (elapsed / spinMs) ** 2 * 320;
      elapsed += gap;
      timer = setTimeout(tick, gap);
    };
    tick();
    return () => clearTimeout(timer);
  });
</script>

<main style="--spin-ms:{spinMs}ms; --c:{meta?.color ?? '#fff'}">
  <div class="stage" class:landed>
    <div class="beams" aria-hidden="true"></div>
    <div class="string" aria-hidden="true"></div>
    <div class="ball" aria-hidden="true"><div class="shine"></div></div>
  </div>
  <div class="cat-slot">
    {#if landed && meta}
      <h1 class="cat">{categoryLabel(view.category!, view.cutoffYear)}!</h1>
    {:else}
      <p class="spinning">Spinning…</p>
    {/if}
  </div>
  <p class="meta">
    {#if view.track === 2 && view.pattern}<PatternGrid cells={view.pattern.cells} label={view.pattern.label} size="2.4em" />{/if}
    {trackHeading(view)}
  </p>
</main>

<style>
  main { min-height: 100%; display: grid; justify-items: center; align-content: center; gap: 3vh; text-align: center; overflow: hidden; }
  .stage { position: relative; width: min(42vh, 42vw); aspect-ratio: 1; margin-top: 6vh; }
  .string { position: absolute; left: 50%; bottom: 100%; width: 3px; height: 12vh; margin-left: -1.5px; background: linear-gradient(#0000, #bbb); }
  .ball {
    position: absolute; inset: 0; border-radius: 50%;
    background:
      repeating-conic-gradient(from 0deg, #e9e6ff 0 7deg, #7a6fa8 7deg 14deg, #c8c0f0 14deg 21deg, #4b3f7c 21deg 30deg);
    box-shadow: 0 0 40px var(--cyan), 0 0 120px rgba(255, 61, 242, 0.55), inset -30px -40px 80px rgba(0, 0, 0, 0.6);
    animation: spin var(--spin-ms) cubic-bezier(0.15, 0.6, 0.3, 1) both;
  }
  .shine {
    position: absolute; inset: 0; border-radius: 50%;
    background:
      repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.25) 0 2px, transparent 2px 10%),
      radial-gradient(circle at 32% 28%, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0) 35%);
  }
  .beams {
    position: absolute; left: 50%; top: 50%; width: 260%; height: 260%; margin: -130% 0 0 -130%;
    background: repeating-conic-gradient(rgba(125, 249, 255, 0.12) 0 4deg, transparent 4deg 22deg, rgba(255, 61, 242, 0.12) 22deg 26deg, transparent 26deg 45deg);
    animation: beams var(--spin-ms) cubic-bezier(0.15, 0.6, 0.3, 1) both;
    transition: background 0.4s;
  }
  .landed .ball { box-shadow: 0 0 50px var(--c), 0 0 140px var(--c), inset -30px -40px 80px rgba(0, 0, 0, 0.6); }
  .cat-slot { min-height: clamp(5rem, 11vw, 10rem); display: flex; align-items: center; }
  .cat {
    margin: 0; font-size: clamp(3.5rem, 9vw, 8.5rem); font-weight: 900; letter-spacing: 0.02em; line-height: 1;
    color: var(--c); text-shadow: 0 0 10px var(--c), 0 0 30px var(--c), 0 0 70px var(--c);
    animation: land 0.55s cubic-bezier(0.2, 1.7, 0.4, 1) both;
  }
  .spinning { margin: 0; font-size: clamp(1.6rem, 3vw, 2.6rem); opacity: 0.7; letter-spacing: 0.2em; text-transform: uppercase; }
  .meta { margin: 0; font-size: clamp(1.2rem, 2.2vw, 2rem); opacity: 0.8; display: flex; align-items: center; gap: 0.6em; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(1440deg); } }
  @keyframes beams { from { transform: rotate(0); } to { transform: rotate(-720deg); } }
  @keyframes land { from { transform: scale(2.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
</style>
